/**
 * useAppStateManager - Single, debounced AppState lifecycle manager
 *
 * IMPORTANT: Use this hook ONCE in your app (typically in AuthContext or App.tsx).
 * Do NOT create multiple instances as this defeats the purpose of preventing
 * duplicate listeners.
 *
 * Purpose:
 * - Consolidates all app lifecycle event handling into one hook
 * - Prevents duplicate AppState listeners
 * - Provides debouncing to avoid rapid-fire callbacks
 * - Works across web (visibilitychange) and native (AppState)
 *
 * @example
 * ```typescript
 * useAppStateManager({
 *   onActive: async () => {
 *     await validateSession();
 *   },
 *   onBackground: () => {
 *     // Pause operations
 *   },
 *   debounceMs: 300,
 *   debug: __DEV__,
 * });
 * ```
 */

import { useEffect, useRef, useCallback } from 'react';
import { AppState, AppStateStatus, Platform } from 'react-native';

interface AppStateManagerConfig {
  /** Callback when app becomes active (foreground) */
  onActive: () => void | Promise<void>;
  /** Callback when app goes to background (optional) */
  onBackground?: () => void | Promise<void>;
  /** Debounce delay in milliseconds (default: 300) */
  debounceMs?: number;
  /** Enable debug logging (default: false) */
  debug?: boolean;
}

// Track instances in dev mode to warn about multiple usages
let instanceCount = 0;

export const useAppStateManager = ({
  onActive,
  onBackground,
  debounceMs = 300,
  debug = false,
}: AppStateManagerConfig): void => {
  // Track previous app state to detect transitions
  const appStateRef = useRef<AppStateStatus | string>(AppState.currentState);
  const debounceTimerRef = useRef<NodeJS.Timeout | null>(null);
  const isProcessingRef = useRef(false);

  // Dev-mode multiple instance detection
  useEffect(() => {
    if (__DEV__) {
      instanceCount++;
      if (instanceCount > 1) {
        console.warn(
          `[AppStateManager] Warning: Multiple instances detected (${instanceCount}). ` +
          'This hook should only be used ONCE in your app to prevent duplicate listeners.'
        );
      }

      return () => {
        instanceCount--;
      };
    }
  }, []);

  const log = useCallback(
    (message: string, ...args: unknown[]) => {
      if (__DEV__ && debug) {
        console.log(`[AppStateManager] ${message}`, ...args);
      }
    },
    [debug]
  );

  // Memoize callbacks to prevent unnecessary effect re-runs
  const handleActive = useCallback(async () => {
    if (isProcessingRef.current) {
      log('Skipping - already processing');
      return;
    }

    isProcessingRef.current = true;
    try {
      log('Executing onActive callback');
      await onActive();
    } catch (error) {
      console.error('[AppStateManager] onActive error:', error);
    } finally {
      isProcessingRef.current = false;
    }
  }, [onActive, log]);

  const handleBackground = useCallback(async () => {
    if (onBackground) {
      try {
        log('Executing onBackground callback');
        await onBackground();
      } catch (error) {
        console.error('[AppStateManager] onBackground error:', error);
      }
    }
  }, [onBackground, log]);

  useEffect(() => {
    const handleStateChange = (nextState: AppStateStatus | string) => {
      const previousState = appStateRef.current;
      const isComingToForeground =
        (previousState === 'background' || previousState === 'inactive' || previousState === 'hidden') &&
        (nextState === 'active' || nextState === 'visible');
      const isGoingToBackground =
        (previousState === 'active' || previousState === 'visible') &&
        (nextState === 'background' || nextState === 'inactive' || nextState === 'hidden');

      log(`State change: ${previousState} → ${nextState}`);
      appStateRef.current = nextState;

      // Clear any pending debounce
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
        debounceTimerRef.current = null;
      }

      if (isComingToForeground) {
        log(`Debouncing onActive (${debounceMs}ms)`);
        debounceTimerRef.current = setTimeout(() => {
          handleActive();
        }, debounceMs);
      } else if (isGoingToBackground) {
        handleBackground();
      }
    };

    // Platform-specific listener setup
    // Check for web ONLY if both Platform.OS === 'web' AND document exists
    const isWeb = Platform.OS === 'web' && typeof document !== 'undefined';

    if (isWeb) {
      log('Setting up web visibility listener');

      const handleVisibilityChange = () => {
        const state = document.visibilityState === 'visible' ? 'visible' : 'hidden';
        handleStateChange(state);
      };

      document.addEventListener('visibilitychange', handleVisibilityChange);

      return () => {
        log('Cleaning up web visibility listener');
        document.removeEventListener('visibilitychange', handleVisibilityChange);
        if (debounceTimerRef.current) {
          clearTimeout(debounceTimerRef.current);
        }
      };
    }

    // Native platform (iOS, Android) or test environment
    log('Setting up native AppState listener');

    const subscription = AppState.addEventListener('change', handleStateChange);

    return () => {
      log('Cleaning up native AppState listener');
      subscription.remove();
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, [handleActive, handleBackground, debounceMs, log]);
};

export default useAppStateManager;
